import ArrowDownIcon from '@ben/icons/arrow-down-s-line';
import SearchIcon from '@ben/icons/search-line';
import {
  CloseButton,
  MultiSelect as MantineMultiSelect,
  type MultiSelectProps as MantineMultiSelectProps,
} from '@mantine/core';
import { t } from 'i18next';
import {
  memo,
  useCallback,
  useMemo,
  useState,
  type ComponentType,
} from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import { GlobalComboboxStyles } from '../combobox';
import InputFeedback from '../input-feedback';
import Icon from '../remix-icon';

export interface MultiSelectProps extends Omit<
  MantineMultiSelectProps<string>,
  'onChange' | 'value' | 'defaultValue' | 'onRemove'
> {
  onSearchChange?: (searchValue: string) => void;
  maxWidth?: string;
  infoMessage?: string;
  errorMessage?: string;
  warningMessage?: string;
  successMessage?: string;
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  onRemove?: (value: string) => void;
}

export const MultiSelect = ({
  onSearchChange,
  maxWidth,
  infoMessage,
  errorMessage,
  warningMessage,
  successMessage,
  onChange,
  onRemove,
  value,
  defaultValue,
  ...rest
}: MultiSelectProps) => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState('');
  const showCloseButton = rest.clearable && value;

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      setSearchValue(searchValue);
      onSearchChange?.(searchValue);
    },
    [onSearchChange]
  );

  const rightSection = useMemo(() => {
    if (showCloseButton) {
      return (
        <CloseButton
          onClick={() => {
            onChange?.([]);
            handleSearchChange('');
          }}
          onMouseDown={(event) => {
            // Prevent input blur
            event.preventDefault();
          }}
          aria-label={t('phrases.clearValue')}
        />
      );
    }
    return (
      <Icon
        icon={ArrowDownIcon}
        color={theme.palette.primary.main}
        className="mantine-Select-chevron"
        size={18}
      />
    );
  }, [
    handleSearchChange,
    onChange,
    rest.clearable,
    showCloseButton,
    theme.palette.primary.main,
    value,
  ]);

  return (
    <Wrapper>
      <GlobalComboboxStyles />
      <StyledMantineMultiSelect
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        comboboxProps={{ withinPortal: false }}
        withScrollArea={false}
        leftSection={rest.searchable ? <Icon icon={SearchIcon} /> : undefined}
        rightSection={rightSection}
        checkIconPosition="right"
        maw={maxWidth}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onRemove={onRemove}
        {...rest}
      />
      <InputFeedback
        infoMessage={infoMessage}
        errorMessage={errorMessage}
        warningMessage={warningMessage}
        successMessage={successMessage}
      />
    </Wrapper>
  );
};

export default memo(MultiSelect);

const StyledMantineMultiSelect = styled(
  MantineMultiSelect as ComponentType<MantineMultiSelectProps<string>>
)`
  ${({ theme }: { theme: ITheme }) => css`
    width: 100%;

    .mantine-MultiSelect-inputField {
      // Vertically center the input inside the enlarged wrapper
      height: 1.8em;
    }

    .mantine-MultiSelect-section {
      // Align search and dropdown icons to the top
      height: ${theme.spacing(4.5)};
    }
  `}
`;

const Wrapper = styled.div`
  width: inherit;
`;
